// Source for the AudioWorklet that captures microphone PCM for the live
// (streaming) AI-interview STT. It is kept as a standalone string so it can be
// (a) injected via AudioWorklet.addModule(blobURL) at runtime and (b) evaluated
// directly in a Node test harness (with fake AudioWorkletProcessor / sampleRate
// globals) so the resampling DSP is actually exercised — see
// __tests__/pcmWorklet.test.js.
//
// The processor converts Float32 mic frames to Int16 PCM and RESAMPLES to
// 16 kHz, because Deepgram's live model is configured for 16 kHz but the
// AudioContext rate is frequently forced to 48 kHz on iOS Safari even when
// 16 kHz is requested. On desktop the context is already 16 kHz, so the ratio
// is 1 and the resample step is a pure pass-through (no behaviour change).
export const PCM_WORKLET_SOURCE = `
  class PcmCaptureProcessor extends AudioWorkletProcessor {
    constructor() {
      super()
      this._frameCount = 0
      this._targetRate = 16000
      this._ratio = sampleRate / this._targetRate // input samples per output sample
      this._pos = 1        // fractional read position (1 = first real input sample)
      this._last = 0       // last input sample of previous block (virtual index 0)
    }
    _resample(f32) {
      if (this._ratio === 1) return f32
      const N = f32.length
      const out = []
      let pos = this._pos
      // Virtual stream: index 0 = last sample of previous block, 1..N = this block.
      while (pos <= N) {
        const i = Math.floor(pos)
        const frac = pos - i
        const a = i === 0 ? this._last : f32[i - 1]
        const b = i < N ? f32[i] : f32[N - 1]
        out.push(a + (b - a) * frac)
        pos += this._ratio
      }
      this._last = f32[N - 1]
      this._pos = pos - N // carry remainder into the next block
      return out
    }
    process(inputs) {
      const input = inputs[0]
      if (input && input[0]) {
        const f32in = input[0]
        let sumSq = 0
        for (let i = 0; i < f32in.length; i++) sumSq += f32in[i] * f32in[i]
        const f32 = this._resample(f32in)
        const len = f32.length
        const i16 = new Int16Array(len)
        for (let i = 0; i < len; i++) {
          const s = Math.max(-1, Math.min(1, f32[i]))
          i16[i] = s < 0 ? s * 0x8000 : s * 0x7fff
        }
        if (len > 0) this.port.postMessage(i16.buffer, [i16.buffer])
        this._frameCount++
        if (this._frameCount % 6 === 0) {
          const rms = Math.sqrt(sumSq / f32in.length)
          this.port.postMessage({ type: 'vad', rms })
        }
      }
      return true
    }
  }
  registerProcessor('pcm-capture-processor', PcmCaptureProcessor)
`;
